<script lang="ts">
  import { differenceInCalendarDays, differenceInHours, format, fromUnixTime, startOfDay } from 'date-fns';
  import { onDestroy } from 'svelte';
  import { programTypeTitle } from '@/Common/constants';
  import { StrToProgramType } from '@/Util/utils';
  import type { MyFitnessStores } from '../stores';

  export let myFitness: MyFitnessStores;
  const { trainingPlan } = myFitness;

  let cardEl: HTMLElement;
  const glassId = `preplan-glass-${Math.random().toString(36).slice(2)}`; // the card can be rendered twice (sidebar + narrow layout)

  let now = new Date();
  const timer = setInterval(() => (now = new Date()), 60 * 1000);
  onDestroy(() => clearInterval(timer));

  $: plan = $trainingPlan;
  $: firstDay = plan?.forecastResults?.days?.[0]?.t;
  $: startDate = firstDay ? startOfDay(fromUnixTime(firstDay)) : null;
  $: programType = plan ? StrToProgramType(plan.program_type, plan.selected_mode ?? 'none') : null;
  $: programTitle = programType != null ? programTypeTitle[programType] : '';

  $: hoursLeft = startDate ? differenceInHours(startDate, now) : 0;
  $: daysLeft = startDate ? differenceInCalendarDays(startDate, now) : 0;
  $: soon = hoursLeft < 24;
  $: amount = soon ? Math.max(hoursLeft, 0) : daysLeft;
  $: unit = soon ? (amount === 1 ? 'hour' : 'hours') : amount === 1 ? 'day' : 'days';

  // Sand level: share of the wait (from when the plan was saved until it starts) that is still left.
  $: created = plan?.created_at ? new Date(plan.created_at) : null;
  $: waitMs = startDate && created ? Math.max(startDate.getTime() - created.getTime(), 1) : null;
  $: leftFraction = startDate ? (waitMs ? Math.min(Math.max((startDate.getTime() - now.getTime()) / waitMs, 0), 1) : 1) : 1;

  // Teal while the start is far off, amber inside a month, red inside a week.
  $: color = hoursLeft <= 7 * 24 ? '#E5484D' : hoursLeft <= 30 * 24 ? '#F5A623' : '#26B897';

  // Hourglass geometry (viewBox 0 0 100 140): each chamber is filled up to 48 units.
  const CHAMBER = 48;
  const BOTTOM = 125;
  const NECK = 66;
  $: topH = leftFraction * CHAMBER;
  $: bottomH = (1 - leftFraction) * CHAMBER;

  function viewProgram() {
    cardEl?.dispatchEvent(new CustomEvent('myfitnesstabchange', { bubbles: true, detail: { tabName: 'program' } }));
  }
</script>

{#if plan?.is_pre_plan && startDate}
  <aside class="w-full shrink-0 space-y-4" bind:this={cardEl}>
    <div class="px-2 text-xs uppercase tracking-wide text-gray-500">Upcoming Training Plan</div>
    <section class="rounded-lg border border-border bg-white p-3 shadow-sm hover:shadow">
      <h2 class="m-0 text-lg font-semibold mb-2">Pre-Plan Maintenance</h2>

      <div class="flex items-center gap-4 py-1">
        <svg viewBox="0 0 100 140" class="h-[110px] w-[80px] shrink-0" role="img" aria-label="{amount} {unit} until your plan starts">
          <defs>
            <clipPath id={glassId}>
              <path d="M22 15 H78 C78 55 57 62 57 70 C57 78 78 85 78 125 H22 C22 85 43 78 43 70 C43 62 22 55 22 15 Z" />
            </clipPath>
          </defs>
          <!-- sand -->
          <g clip-path="url(#{glassId})" fill={color}>
            <rect x="0" y={NECK - topH} width="100" height={topH > 0 ? topH + 4 : 0} opacity="0.9" />
            <rect x="0" y={BOTTOM - bottomH} width="100" height={bottomH} opacity="0.9" />
          </g>
          <!-- falling sand: purely decorative, the sand levels above are not affected -->
          {#if leftFraction > 0}
            <line class="stream" x1="50" y1={NECK} x2="50" y2={BOTTOM - bottomH} stroke={color} stroke-width="1.6" />
            {#each [0, 0.4, 0.8] as delay}
              <circle class="grain" cx="50" cy={NECK} r="1.6" fill={color} style="--drop: {BOTTOM - bottomH - NECK}px; animation-delay: {delay}s" />
            {/each}
          {/if}
          <!-- glass and frame -->
          <path d="M22 15 H78 C78 55 57 62 57 70 C57 78 78 85 78 125 H22 C22 85 43 78 43 70 C43 62 22 55 22 15 Z" fill="none" stroke="#6b7280" stroke-width="2.5" stroke-linejoin="round" />
          <rect x="14" y="8" width="72" height="8" rx="4" fill="#6b7280" />
          <rect x="14" y="124" width="72" height="8" rx="4" fill="#6b7280" />
        </svg>

        <div class="flex flex-col">
          <span class="text-4xl font-bold leading-none" style="color: {color}">{amount}</span>
          <span class="text-lg font-semibold text-gray-500">{unit}</span>
          <span class="text-sm text-gray-500">until your plan starts</span>
        </div>
      </div>

      <div class="subtext mt-2 text-gray-500">
        Program : <strong>{programTitle}</strong><br />
        Starts : <strong>{format(startDate, 'EEE MMM d, yyyy')}</strong>
      </div>

      <button class="mt-3 w-full rounded border border-border px-2 py-1 text-sm hover:bg-accent" on:click={viewProgram}>View plan</button>
    </section>
  </aside>
{/if}

<style>
  .stream {
    stroke-dasharray: 3 5;
    animation: preplan-flow 0.5s linear infinite;
  }
  .grain {
    opacity: 0;
    animation: preplan-fall 1.2s linear infinite;
  }
  @keyframes preplan-flow {
    to {
      stroke-dashoffset: -8;
    }
  }
  @keyframes preplan-fall {
    0% {
      transform: translateY(0);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      transform: translateY(var(--drop));
      opacity: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .stream,
    .grain {
      animation: none;
    }
    .grain {
      opacity: 0;
    }
  }
</style>
